import React, { createContext, useContext, useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, get, set, push, remove, onValue, off } from 'firebase/database';
import { useAuth } from './AuthContext';

const AnnouncementContext = createContext();

export function useAnnouncements() {
  return useContext(AnnouncementContext);
}

export const ANNOUNCEMENT_CATEGORIES = [
  'General',
  'ROTC',
  'CWTS',
  'Important',
  'Event'
];

export function AnnouncementProvider({ children }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, isAdmin } = useAuth();

  // Fetch announcements from Firebase using a realtime listener
  useEffect(() => {
    setLoading(true);
    setError('');
    
    const announcementsRef = ref(database, 'announcements');
    
    // Create a realtime listener for announcements
    const unsubscribe = onValue(announcementsRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const announcementsData = snapshot.val();
          const announcementsList = Object.keys(announcementsData)
            .map(key => {
              const announcementData = announcementsData[key];
              // Ensure all required fields exist with default values if missing
              return {
                id: key,
                title: announcementData.title || 'Untitled Announcement',
                content: announcementData.content || 'No content provided',
                category: ANNOUNCEMENT_CATEGORIES.includes(announcementData.category) 
                  ? announcementData.category 
                  : 'General',
                createdAt: announcementData.createdAt || Date.now(),
                updatedAt: announcementData.updatedAt || Date.now(),
                createdBy: announcementData.createdBy || 'unknown',
                authorEmail: announcementData.authorEmail || 'unknown',
                imageBase64: announcementData.imageBase64 || null
              };
            })
            .sort((a, b) => b.createdAt - a.createdAt); // Sort by most recent first
          
          setAnnouncements(announcementsList);
        } else {
          // No announcements exist, but this is not an error
          setAnnouncements([]);
        }
        setError('');
      } catch (err) {
        console.error('Error processing announcements data:', err);
        setError('Failed to process announcements data');
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('Error fetching announcements:', err);
      setError('Failed to load announcements: ' + err.message);
      setLoading(false);
    });
    
    // Clean up the listener when the component unmounts
    return () => {
      off(announcementsRef);
    };
  }, []);

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Add a new announcement
  const addAnnouncement = async (title, content, category, imageFile) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can add announcements');
    }

    try {
      // Validate category to ensure it's not undefined
      const validCategory = ANNOUNCEMENT_CATEGORIES.includes(category) ? category : 'General';
      
      // Convert image to base64 if provided
      let imageBase64 = null;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }
      
      const announcementsRef = ref(database, 'announcements');
      const newAnnouncementRef = push(announcementsRef);
      
      const announcement = {
        title,
        content,
        category: validCategory,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: currentUser.uid,
        authorEmail: currentUser.email,
        imageBase64
      };
      
      await set(newAnnouncementRef, announcement);
      
      // No need to update local state as the realtime listener will do it
      return { success: true, id: newAnnouncementRef.key };
    } catch (err) {
      console.error('Error adding announcement:', err);
      throw err;
    }
  };

  // Edit an existing announcement
  const editAnnouncement = async (id, { title, content, category, imageFile, keepExistingImage }) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can edit announcements');
    }

    try {
      const announcementRef = ref(database, `announcements/${id}`);
      
      // Get the current data first
      const snapshot = await get(announcementRef);
      if (!snapshot.exists()) {
        throw new Error('Announcement not found');
      }
      
      const currentData = snapshot.val();
      
      // Validate category to ensure it's valid
      let validCategory = currentData.category;
      if (category !== undefined) {
        validCategory = ANNOUNCEMENT_CATEGORIES.includes(category) ? category : 'General';
      }
      
      // Handle image updates
      let imageBase64 = currentData.imageBase64;
      
      // If keepExistingImage is false or there's a new image, handle image replacement
      if (!keepExistingImage || imageFile) {
        // Clear existing image if not keeping it
        if (!keepExistingImage) {
          imageBase64 = null;
        }
        
        // Convert new image to base64 if provided
        if (imageFile) {
          imageBase64 = await convertToBase64(imageFile);
        }
      }
      
      // Update only the provided fields
      const updatedData = {
        ...currentData,
        title: title !== undefined ? title : currentData.title,
        content: content !== undefined ? content : currentData.content,
        category: validCategory,
        updatedAt: Date.now(),
        imageBase64
      };
      
      await set(announcementRef, updatedData);
      
      // No need to update local state as the realtime listener will do it
      return { success: true };
    } catch (err) {
      console.error('Error editing announcement:', err);
      throw err;
    }
  };

  // Delete an announcement
  const deleteAnnouncement = async (id) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can delete announcements');
    }

    try {
      // Delete the announcement from the database
      const announcementRef = ref(database, `announcements/${id}`);
      await remove(announcementRef);
      
      // No need to update local state as the realtime listener will do it
      return { success: true };
    } catch (err) {
      console.error('Error deleting announcement:', err);
      throw err;
    }
  };

  // Get announcements by category
  const getAnnouncementsByCategory = (category) => {
    if (category === 'All') {
      return announcements;
    }
    return announcements.filter(announcement => announcement.category === category);
  };

  const value = {
    announcements,
    loading,
    error,
    addAnnouncement,
    editAnnouncement,
    deleteAnnouncement,
    getAnnouncementsByCategory
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
} 